import { css } from '@emotion/react';
import { useQuery, QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { t, Trans } from 'i18n';
import React, { PropsWithChildren, useState } from 'react';
import { formatCurrency, formatPoints } from 'shared';

import { LoadingIndicator } from '../components';
import { getConditionsOfCarriageUrl, getTermsAndConditionsUrl } from '../utils';
import { bookingAxios } from '../utils/axiosInstance';

import { Divider } from './Divider';
import { CheckboxField } from './forms';
import { LegacyCard } from './LegacyCard';
import { SasIcon } from './SasIcon';
import { SasModal } from './SasModal';
import { theme } from './theme';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
  `,

  termsErrorContainer: css`
    display: flex;
    gap: ${theme.spacing.sm};
    color: ${theme.colors.danger};
  `,

  termsErrorText: css`
    font: ${theme.typography.pRebook};
  `,
  cartContainer: css`
    background: ${theme.colors.white};
    border-radius: ${theme.borders.radius.lg};
    outline: 1px solid ${theme.colors.border};
    padding: 16px;
  `,
  children: css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    > div {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  `,
  checkboxContainer: css`
    display: flex;
    align-items: center;
  `,
  warningText: css`
    display: flex;
    gap: ${theme.spacing.sm};
    margin-top: ${theme.spacing.md};
    color: ${theme.colors.danger};
    width: 100%;
  `,
  buttonOverride: css`
    text-decoration: underline;
    color: var(--color-link);
    background: none;
    border: none;
    padding: 0;
    font: var(--font-p);
    text-transform: none;
    min-height: 0;
    min-width: 0;
    font-size: 14px;
  `,
  totalPrice: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
    > h3 {
      margin: 0;
    }
    h3:nth-of-type(2) {
      margin: 0;
      color: ${theme.colors.text};
    }
  `,
  totalPriceValue: css`
    display: block;
    text-align: right;
    font-size: ${theme.fontSize.lg};
    font-weight: ${theme.fontWeight.black};
  `,
  dividerTopMargin: css`
    margin-top: ${theme.spacing.sm};
  `,
  dividerBottomMargin: css`
    margin-bottom: ${theme.spacing.md};
  `,
  fontSizeSm: css`
    font-size: ${theme.fontSize.sm};
  `,
  marginTopMd: css`
    margin-top: ${theme.spacing.md};
  `,
  lineThrough: css`
    text-decoration: line-through;
    font-size: ${theme.fontSize.lg};
    font-weight: ${theme.fontWeight.black};
  `,
  discountContainer: css`
    display: flex;
    gap: 8px;
  `,
};

type Props = {
  pointOfSale: string;
  amount: number;
  points?: { totalPoints?: number; discountedPoints?: number };
  children?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  handleSubmit: () => void;
};

export function CheckoutCard({
  pointOfSale,
  amount,
  points,
  children,
  className,
  isLoading = false,
  handleSubmit,
}: Props) {
  const [isTermsApproved, setIsTermsApproved] = useState(false);
  const [showTaCModal, setShowTaCModal] = useState(false);
  const [showAcceptTermsMessage, setShowAcceptTermsMessage] = useState(false);

  const hasDiscount = (points?.discountedPoints ?? 0) > 0;

  const handleTermsChange = () => {
    setIsTermsApproved((prevValue: boolean) => !prevValue);
  };

  const handleClick = () => {
    if (isTermsApproved) {
      handleSubmit();
      return;
    }

    setShowAcceptTermsMessage(true);
  };

  const buttonText = amount <= 0 ? t('common:confirm') : t('common:pay');

  return (
    <WithQueryClientProvider>
      <LegacyCard css={styles.container} className={className}>
        {children && (
          <>
            <div css={styles.children}>{children}</div>
            <Divider />
          </>
        )}

        <div css={styles.totalPrice}>
          <h3>{t('common:total')}</h3>
          <p>
            {points?.totalPoints !== undefined ? (
              <>
                <div css={styles.discountContainer}>
                  {hasDiscount ? (
                    <>
                      <span css={styles.totalPriceValue}>
                        {formatPoints(points.discountedPoints ?? 0, pointOfSale)}
                      </span>
                      <span css={styles.lineThrough}>{formatPoints(points.totalPoints ?? 0, pointOfSale)}</span>
                    </>
                  ) : (
                    <span css={styles.totalPriceValue}>{formatPoints(points.totalPoints ?? 0, pointOfSale)}</span>
                  )}
                </div>
                <span css={styles.totalPriceValue}>{formatCurrency(amount, pointOfSale)}</span>
              </>
            ) : (
              <span css={styles.totalPriceValue}>{formatCurrency(amount, pointOfSale)}</span>
            )}
          </p>
        </div>

        <Divider />

        <div css={styles.checkboxContainer}>
          <CheckboxField name="terms-conditions" checked={isTermsApproved} onChange={handleTermsChange}>
            <span css={styles.fontSizeSm}>
              <Trans i18nKey="payment.agree.label">
                I agree to the{' '}
                <button onClick={() => setShowTaCModal(true)} css={styles.buttonOverride}>
                  Terms & Conditions
                </button>
              </Trans>
            </span>
          </CheckboxField>
        </div>

        <TermsAndConditionModal open={showTaCModal} handleClose={() => setShowTaCModal(false)} />

        {!isTermsApproved && showAcceptTermsMessage && (
          <div css={styles.termsErrorContainer}>
            <SasIcon color={theme.colors.danger} name="cl--alert-circle" />
            <p css={styles.termsErrorText}>{t('payment.agree.warning')}</p>
          </div>
        )}

        <button id="pay-button" onClick={handleClick} data-testid="go to confirmation button">
          {isLoading ? (
            <>
              {buttonText} <LoadingIndicator />
            </>
          ) : (
            buttonText
          )}
        </button>
      </LegacyCard>
    </WithQueryClientProvider>
  );
}

// Creating a default QueryClient instance for apps that has not set up a QueryClientProvider
const defaultQueryClient = new QueryClient();

function WithQueryClientProvider({ children }: PropsWithChildren) {
  let queryClient;
  try {
    queryClient = useQueryClient();
  } catch (e) {
    queryClient = defaultQueryClient;
  }

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

function TermsAndConditionModal({ open, handleClose }: { open: boolean; handleClose: () => void }) {
  const { data: termsAndCondition } = useTermsAndConditions();

  return (
    <SasModal open={open} handleClose={handleClose}>
      {termsAndCondition !== undefined ? (
        <div
          dangerouslySetInnerHTML={{
            __html: termsAndCondition,
          }}
        />
      ) : (
        <>
          <div css={styles.warningText} id="warningTextContainer">
            <SasIcon name="cl--alert-circle" color={theme.colors.danger} />
            <span>{t('error.unableToDisplayTerms.title')}</span>
          </div>
          <div css={styles.marginTopMd}>
            <a href={getConditionsOfCarriageUrl()} target="_blank" rel="noreferrer">
              {t('error.unableToDisplayTerms.message')} <SasIcon name="cl--open-in-new" color="var(--color-link)" />
            </a>
          </div>
        </>
      )}
    </SasModal>
  );
}

const getTermsAndConditions = async (): Promise<string> => {
  const url = getTermsAndConditionsUrl();
  const { data } = await bookingAxios.get<string>(url);

  return data;
};

const validateHTML = (htmlString: string): boolean =>
  new DOMParser().parseFromString(htmlString, 'text/html').body.textContent !== '';

const useTermsAndConditions = () => {
  return useQuery({
    queryKey: ['terms-and-conditions'],
    queryFn: async () => {
      const data = await getTermsAndConditions();

      const isValidHTML = validateHTML(data);
      if (isValidHTML) return data;
    },
  });
};
